import React, { useEffect, useState, useRef } from "react";
import { Button } from "../../button";
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';

function ImageCrop({ imageSrc, onCropComplete, onCancel, t, fileToUpload }) {
    const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB

    const [crop, setCrop] = useState();
    const [completedCrop, setCompletedCrop] = useState(null);
    const [initialCrop, setInitialCrop] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [fileSizeError, setFileSizeError] = useState(null);
    const imgRef = useRef(null);

    const isCropEqual = (crop1, crop2) => {
        if (!crop1 || !crop2) return false;
        return crop1.x === crop2.x &&
            crop1.y === crop2.y &&
            crop1.width === crop2.width &&
            crop1.height === crop2.height;
    };

    useEffect(() => {
        if (imageSrc && imgRef.current) {
            const { width, height } = imgRef.current;
            const initialCropData = {
                unit: 'px',
                x: 0,
                y: 0,
                width: width,
                height: height
            };
            setCrop(initialCropData);
            setCompletedCrop(initialCropData);
            setInitialCrop(initialCropData);
        }
    }, [imageSrc]);

    const getCroppedImg = async (image, crop, mimeType = 'image/jpeg') => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        const scaleX = image.naturalWidth / image.width;
        const scaleY = image.naturalHeight / image.height;

        // Utiliser les dimensions réelles de l'image pour ne pas perdre en résolution
        canvas.width = crop.width * scaleX;
        canvas.height = crop.height * scaleY;

        ctx.drawImage(
            image,
            crop.x * scaleX,
            crop.y * scaleY,
            crop.width * scaleX,
            crop.height * scaleY,
            0,
            0,
            canvas.width,
            canvas.height
        );

        return new Promise((resolve) => {
            canvas.toBlob((blob) => {
                resolve(blob);
            }, mimeType, mimeType === 'image/jpeg' ? 0.85 : 1);
        });
    };

    const handleCropAndSave = async () => {
        if (!completedCrop || !imgRef.current) return;

        try {
            setIsProcessing(true);
            setFileSizeError(null);

            // Si le crop n'a pas été modifié, renvoyer le fichier original sans passer par le canvas
            if (isCropEqual(initialCrop, completedCrop)) {
                if (fileToUpload.size > MAX_FILE_SIZE) {
                    setIsProcessing(false);
                    setFileSizeError(t("Le fichier dépasse la taille maximale autorisée de 5 Mo."));
                    return;
                }
                setIsProcessing(false);
                onCropComplete(fileToUpload);
                return;
            }

            const originalMimeType = fileToUpload.type;
            const croppedBlob = await getCroppedImg(imgRef.current, completedCrop, originalMimeType);

            if (croppedBlob.size > MAX_FILE_SIZE) {
                setIsProcessing(false);
                setFileSizeError(t("Le fichier dépasse la taille maximale autorisée de 5 Mo."));
                return;
            }

            const finalFile = new File([croppedBlob], fileToUpload.name, { type: originalMimeType });

            setIsProcessing(false);
            onCropComplete(finalFile);

        } catch (e) {
            console.error('Error cropping image:', e);
            setIsProcessing(false);
            onCancel(t("Erreur lors du recadrage de l'image, veuillez réessayer."));
        }
    };

    const handleCancel = () => {
        setCrop();
        setCompletedCrop(null);
        onCancel();
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-70 z-50 flex items-center justify-center p-2 sm:p-8">
            <div className="bg-white rounded-lg shadow-2xl max-w-6xl w-full max-h-[95vh] sm:max-h-[90vh] flex flex-col">
                <div className="px-4 py-3 sm:px-6 sm:py-4 border-b border-gray-200">
                    <h2 className="text-lg sm:text-xl font-semibold text-gray-900">{t("Crop the image")}</h2>
                    <p className="text-xs sm:text-sm text-gray-500 mt-1">{t("Edit the image before submitting it")}</p>
                    {fileSizeError && (
                        <p className="text-xs sm:text-sm text-red-600 mt-2 font-medium">{fileSizeError}</p>
                    )}
                </div>

                <div className="flex-1 flex flex-col md:flex-row overflow-hidden">
                    <div className="flex-1 flex items-center justify-center p-3 sm:p-6 overflow-auto">
                        <ReactCrop
                            crop={crop}
                            onChange={(c) => setCrop(c)}
                            onComplete={(c) => setCompletedCrop(c)}
                            aspect={undefined}
                        >
                            <img
                                ref={imgRef}
                                src={imageSrc}
                                className="max-w-full max-h-[60vh] object-contain w-auto h-auto"
                                alt="Crop"
                            />
                        </ReactCrop>
                    </div>

                    <hr className="hidden md:block border-dashed w-px h-auto border-gray-200"></hr>

                    <div className="hidden md:flex flex-1 flex-col p-6">
                        <div className="flex-1 flex items-center justify-center overflow-hidden">
                            <div className="text-center">
                                <p className="text-sm font-medium text-gray-700 mb-4">{t("Preview")}</p>
                                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                                    {completedCrop && imgRef.current ? (
                                        <canvas
                                            ref={(canvas) => {
                                                if (!canvas || !imgRef.current || !completedCrop) return;
                                                const ctx = canvas.getContext('2d');
                                                const image = imgRef.current;
                                                const scaleX = image.naturalWidth / image.width;
                                                const scaleY = image.naturalHeight / image.height;
                                                canvas.width = completedCrop.width;
                                                canvas.height = completedCrop.height;
                                                ctx.drawImage(
                                                    image,
                                                    completedCrop.x * scaleX,
                                                    completedCrop.y * scaleY,
                                                    completedCrop.width * scaleX,
                                                    completedCrop.height * scaleY,
                                                    0,
                                                    0,
                                                    completedCrop.width,
                                                    completedCrop.height
                                                );
                                            }}
                                            className="max-w-full max-h-[50vh] object-contain"
                                        />
                                    ) : (
                                        <div className="text-gray-400 py-20 px-10">
                                            <p className="text-sm">{t("Select an area to see preview")}</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="mt-6 flex gap-3 justify-end flex-col">
                            <Button
                                onClick={handleCropAndSave}
                                disabled={isProcessing || !completedCrop}
                                className="cursor-pointer"
                            >
                                {isProcessing ? t("Chargement...") : isCropEqual(initialCrop, completedCrop) ? t("Upload") : t("Recadrer et télécharger")}
                            </Button>
                            <Button
                                onClick={handleCancel}
                                disabled={isProcessing}
                                className="cursor-pointer"
                            >
                                {t("Cancel")}
                            </Button>
                        </div>
                    </div>
                </div>

                <div className="md:hidden p-3 border-t border-gray-200 flex flex-col gap-2">
                    <Button
                        onClick={handleCropAndSave}
                        disabled={isProcessing || !completedCrop}
                        className="text-sm py-2"
                    >
                        {isProcessing ? t("Chargement...") : isCropEqual(initialCrop, completedCrop) ? t("Upload") : t("Recadrer et télécharger")}
                    </Button>
                    <Button
                        onClick={handleCancel}
                        disabled={isProcessing}
                        className="text-sm py-2"
                    >
                        {t("Cancel")}
                    </Button>
                </div>
            </div>
        </div>
    );
}

export default ImageCrop;